/**
 * How a recipe document's enums read to a human.
 *
 * The document stores kebab-case machine values (`'dry-hop'`, `'water-agent'`,
 * `'infusion'`) and every read-only surface — the share page, the brew sheet —
 * has to turn them into words. Two of those surfaces had grown their own
 * `HOP_USE_LABELS` and were printing every *other* enum raw, which is how a
 * shared recipe came to say "infusion" and "liquid" in the middle of otherwise
 * typeset tables. One module, so a new enum member fails the type check here
 * rather than shipping lowercase.
 *
 * The **editor's** selects are deliberately not consumers: an editor option
 * list carries `{value, label}` pairs in the order the menu offers them, which
 * is ordering information a record cannot hold. Those lists live with their
 * tables; these maps are for surfaces that only print.
 */

import type { RecipeDoc } from '@beerwright/commands';

type RecipeHop = RecipeDoc['hops'][number];
type RecipeYeast = RecipeDoc['yeasts'][number];
type RecipeMisc = RecipeDoc['miscs'][number];
type RecipeMashStep = RecipeDoc['mashSteps'][number];

/** Hop timing, as the recipient of a recipe reads it. */
export const HOP_USE_LABELS: Readonly<Record<RecipeHop['use'], string>> = {
  boil: 'Boil',
  'first-wort': 'First wort',
  whirlpool: 'Whirlpool',
  'dry-hop': 'Dry hop',
  keg: 'Keg',
};

/** What the hop physically is. */
export const HOP_FORM_LABELS: Readonly<Record<RecipeHop['form'], string>> = {
  pellet: 'Pellet',
  whole: 'Whole',
  plug: 'Plug',
  cryo: 'Cryo',
  'co2-extract': 'CO₂ extract',
  'iso-extract': 'Pre-isomerized',
  'aroma-extract': 'Aroma extract',
};

/** How the yeast is packaged. */
export const YEAST_FORM_LABELS: Readonly<Record<RecipeYeast['form'], string>> = {
  dry: 'Dry',
  liquid: 'Liquid',
};

/** What kind of thing a misc addition is. */
export const MISC_TYPE_LABELS: Readonly<Record<RecipeMisc['type'], string>> = {
  'water-agent': 'Water agent',
  acid: 'Acid',
  alkali: 'Alkali',
  fining: 'Fining',
  nutrient: 'Nutrient',
  'water-treatment': 'Water treatment',
  enzyme: 'Enzyme',
  spice: 'Spice',
  'hop-flavour': 'Hop flavor',
  other: 'Other',
};

/** Where in the process a misc addition goes in. */
export const MISC_USE_LABELS: Readonly<Record<RecipeMisc['use'], string>> = {
  water: 'Water',
  mash: 'Mash',
  sparge: 'Sparge',
  boil: 'Boil',
  whirlpool: 'Whirlpool',
  primary: 'Primary',
  secondary: 'Secondary',
  packaging: 'Packaging',
};

/** How a mash step is reached. */
export const MASH_STEP_TYPE_LABELS: Readonly<Record<RecipeMashStep['type'], string>> = {
  infusion: 'Infusion',
  temperature: 'Temperature',
  decoction: 'Decoction',
  sparge: 'Sparge',
};
